import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import useApiData from '../../services/useApiData.js';
import api from '../../services/axiosInterceptor.js';
import SnackbarEx from '../Helpers/SnackbarEx.jsx';

const statusCopy = (ebay) => {
    if (ebay?.source === 'location') {
        return 'Connected. The refresh token is stored on this location.';
    }
    if (ebay?.source === 'env') {
        return 'Already connected. Reconnect to store the token on this location instead of the server environment.';
    }
    return 'Not connected.';
};

export default function Integrations() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { data, load: isLoading, refetch } = useApiData('api/broylesllc/integrations');
    const [connecting, setConnecting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', color: 'success' });
    const exchangedCode = useRef(null);

    const ebay = data?.ebay;
    const ebayResult = searchParams.get('ebay');

    useEffect(() => {
        if (!ebayResult || exchangedCode.current === ebayResult) return;
        exchangedCode.current = ebayResult;

        const messages = {
            connected: ['eBay reconnected.', 'success'],
            denied: ['eBay authorization was declined.', 'error'],
            missing: ['eBay did not return an authorization code.', 'error'],
            error: ['Failed to reconnect eBay.', 'error'],
        };
        const [message, color] = messages[ebayResult] || ['eBay connection updated.', 'info'];
        setSnackbar({ open: true, message, color });
        if (ebayResult === 'connected') refetch();

        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('ebay');
            next.set('page', '4');
            return next;
        }, { replace: true });
    }, [ebayResult, refetch, setSearchParams]);

    const handleReconnect = async () => {
        setConnecting(true);
        try {
            const { data: auth } = await api.get('/api/broylesllc/integrations/ebay/authorize');
            if (!auth?.url) throw new Error('Missing eBay authorize URL');
            window.location.assign(auth.url);
        } catch (err) {
            console.error(err);
            const msg = err?.response?.data?.message ?? 'Failed to start eBay reconnect.';
            setSnackbar({ open: true, message: msg, color: 'error' });
            setConnecting(false);
        }
    };

    if (isLoading && !ebay) {
        return (
            <div className="d-flex justify-content-center pt-5">
                <CircularProgress size={24} />
            </div>
        );
    }

    return (
        <>
            <div className="d-flex justify-content-center pt-4 pb-4">
                <Card variant="outlined" sx={{ maxWidth: 520, width: '100%', borderRadius: 3 }} elevation={0}>
                    <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                            <Stack direction="row" alignItems="center" gap={1}>
                                <StorefrontOutlinedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                                <Typography variant="subtitle1" fontWeight={500}>eBay</Typography>
                            </Stack>
                            <Chip
                                size="small"
                                label={ebay?.connected ? 'Connected' : 'Not connected'}
                                color={ebay?.connected ? 'success' : 'default'}
                                variant={ebay?.connected ? 'filled' : 'outlined'}
                            />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                            {statusCopy(ebay)}
                            {ebay?.accountId ? ` Account ${ebay.accountId}.` : ''}
                        </Typography>
                        <Button
                            variant="contained"
                            disableElevation
                            disabled={connecting}
                            onClick={handleReconnect}
                            sx={{
                                textTransform: 'none',
                                borderRadius: 2,
                                fontWeight: 500,
                                backgroundColor: '#102A43',
                                '&:hover': { backgroundColor: '#1a3d5c' },
                            }}
                        >
                            {connecting
                                ? <CircularProgress size={16} sx={{ color: 'white' }} />
                                : ebay?.connected ? 'Reconnect' : 'Connect'}
                        </Button>
                    </CardContent>
                </Card>
            </div>
            <SnackbarEx
                open={snackbar.open}
                message={snackbar.message}
                color={snackbar.color}
                close={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            />
        </>
    );
}
